import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Select } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from "../config/api.js";
import "./RestaurantOrderTicket.css";

const SERVICE_MODE_LABELS = {
  dine_in: "Sur place",
  takeaway: "A emporter",
  delivery: "Livraison",
};

const FLOW_STATUS_LABELS = {
  draft: "Brouillon",
  confirmed: "Confirmee",
  sent_to_kitchen: "Envoyee en cuisine",
  in_preparation: "En preparation",
  ready: "Prete",
  served: "Servie",
  closed: "Cloturee",
  cancelled: "Annulee",
};

const KITCHEN_STATUS_LABELS = {
  pending: "En attente",
  ready: "Prete pour production",
  ready_for_production: "Prete pour production",
  partial: "Partielle",
  blocked: "Bloquee",
  not_required: "Hors production",
  converted: "Produite",
  cancelled: "Annulee",
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${date.toLocaleDateString("fr-FR")} ${date.toLocaleTimeString(
    "fr-FR",
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  )}`;
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatQuantity = (value, unitShortName) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  const rendered = numeric % 1 === 0 ? String(numeric) : numeric.toFixed(2);
  return unitShortName ? `${rendered} ${unitShortName}` : rendered;
};

const resolveLogoUrl = (logo) => {
  if (!logo) return "";
  if (logo.startsWith("http://") || logo.startsWith("https://")) return logo;
  if (logo.includes("uploads/warehouses") || logo.startsWith("/uploads/warehouses")) {
    return `${API_CONFIG.BASE_URL}${logo.startsWith("/") ? "" : "/"}${logo}`;
  }
  return logo.startsWith("/") ? logo : `/${logo}`;
};

const getStatusLabel = (status, labels) => labels[status] || status || "-";

const RestaurantOrderTicket = ({ visible, order, onClose }) => {
  const [warehouseInfo, setWarehouseInfo] = useState(null);
  const [ticketWidthMm, setTicketWidthMm] = useState(() => {
    const storedRestaurantWidth = Number(
      window.localStorage.getItem("restaurantOrderTicketWidthMm")
    );
    if (storedRestaurantWidth === 58 || storedRestaurantWidth === 80) {
      return storedRestaurantWidth;
    }

    const storedPosWidth = Number(window.localStorage.getItem("posReceiptWidthMm"));
    return storedPosWidth === 58 || storedPosWidth === 80 ? storedPosWidth : 80;
  });

  useEffect(() => {
    window.localStorage.setItem("restaurantOrderTicketWidthMm", String(ticketWidthMm));
  }, [ticketWidthMm]);

  useEffect(() => {
    if (!visible || !order?.warehouse_id) {
      setWarehouseInfo(null);
      return;
    }

    let cancelled = false;
    axios
      .get(`${API_CONFIG.BASE_URL}/api/warehouses/${order.warehouse_id}`)
      .then((response) => {
        if (!cancelled) {
          setWarehouseInfo(response.data || null);
        }
      })
      .catch((error) => {
        console.error("Erreur chargement établissement pour ticket commande:", error);
        if (!cancelled) {
          setWarehouseInfo(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [visible, order?.warehouse_id]);

  const ticketData = useMemo(() => {
    const items = Array.isArray(order?.items) ? order.items : [];
    const totalQuantity = items.reduce(
      (sum, item) => sum + (Number(item?.quantity) || 0),
      0
    );

    return {
      items,
      totalQuantity,
      totalItems: Number(order?.total_items) || items.length,
      total: Number(order?.total) || 0,
      subtotal: Number(order?.subtotal) || Number(order?.total) || 0,
      logoUrl: resolveLogoUrl(
        warehouseInfo?.logo || warehouseInfo?.logo_url || order?.warehouse_logo
      ),
      warehouseName:
        warehouseInfo?.name || order?.warehouse_name || order?.warehouse?.name || "Établissement",
      warehouseAddress:
        warehouseInfo?.address || order?.warehouse?.address || "Adresse non renseignee",
      warehousePhone:
        warehouseInfo?.phone || order?.warehouse?.phone || "Telephone non renseigne",
      warehouseEmail: warehouseInfo?.email || order?.warehouse?.email || "",
      serviceMode: getStatusLabel(order?.service_mode, SERVICE_MODE_LABELS),
      flowStatus: getStatusLabel(order?.flow_status, FLOW_STATUS_LABELS),
      kitchenStatus: getStatusLabel(order?.kitchen_status, KITCHEN_STATUS_LABELS),
    };
  }, [order, warehouseInfo]);

  if (!visible || !order) {
    return null;
  }

  const isNarrowPaper = ticketWidthMm <= 60;
  const previewMaxWidth = isNarrowPaper ? "220px" : "280px";
  const baseFontSize = isNarrowPaper ? 14 : 13;
  const itemFontSize = isNarrowPaper ? 12 : 11;
  const infoFontSize = isNarrowPaper ? 12 : 11;
  const logoWidth = isNarrowPaper ? 90 : 110;

  const buildTicketHtml = () => {
    const itemRows = ticketData.items
      .map((item, index) => {
        const quantity = Number(item?.quantity) || 0;
        const unitPrice = Number(item?.unit_price) || 0;
        const subtotal = quantity * unitPrice;
        const course = item?.course_label
          ? `<div class="ticket-item-note">${escapeHtml(item.course_label)}</div>`
          : "";
        const note = item?.notes
          ? `<div class="ticket-item-note">Note: ${escapeHtml(item.notes)}</div>`
          : "";

        return `
          <tr class="ticket-item">
            <td>${index + 1}</td>
            <td>
              ${escapeHtml(item?.product_name || `Produit ${item?.product_id || ""}`)}
              ${course}
              ${note}
            </td>
            <td>${escapeHtml(formatQuantity(quantity, item?.unit_short_name))}</td>
            <td class="text-right">${formatAmount(unitPrice)}</td>
            <td class="text-right">${formatAmount(subtotal)}</td>
          </tr>
        `;
      })
      .join("");

    return `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="utf-8" />
        <title>Ticket de commande</title>
        <style>
          @page { size: ${ticketWidthMm}mm auto; margin: 0; }
          * { box-sizing: border-box; }
          html, body { width: ${ticketWidthMm}mm; }
          body {
            font-family: "Courier New", "Consolas", monospace;
            font-size: ${baseFontSize}px;
            font-weight: 700;
            width: ${ticketWidthMm}mm;
            margin: 0;
            padding: 2mm;
            line-height: 1.35;
            color: #000;
            background: #fff;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .ticket-header { text-align: center; margin: 2mm 0 1mm; }
          .ticket-logo { width: ${logoWidth}px; max-width: 100%; height: auto; margin: 1mm auto; display: block; }
          .ticket-company { text-align: center; margin: 1mm 0 2mm; border-bottom: 1px solid #000; padding-bottom: 2mm; }
          .ticket-company h2 { margin: 0.5mm 0; font-size: 14px; font-weight: 700; }
          .ticket-company p { margin: 0.5mm 0; font-size: ${infoFontSize}px; }
          .ticket-title { text-align: center; margin: 2mm 0; font-size: 12px; font-weight: 700; letter-spacing: 0.3px; }
          .ticket-info { margin: 1.5mm 0; font-size: ${infoFontSize}px; }
          .ticket-info table { width: 100%; table-layout: fixed; border-collapse: collapse; }
          .ticket-info td { padding: 0.5mm 0; vertical-align: top; }
          .divider { border-top: 1px dashed #000; margin: 2mm 0; }
          .ticket-items table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: ${itemFontSize}px; }
          .ticket-items thead td { font-weight: 700; padding: 0.8mm 0; border-bottom: 1px solid #000; }
          .ticket-item { border-bottom: 1px solid #000; }
          .ticket-item td { padding: 0.8mm 0; vertical-align: top; }
          .ticket-item td:nth-child(2) { word-break: break-word; }
          .ticket-item-note { margin-top: 0.6mm; font-size: 9px; font-weight: 400; }
          .ticket-summary { margin: 2mm 0; border-top: 1px solid #000; padding-top: 1.5mm; }
          .ticket-summary table { width: 100%; font-size: 11px; }
          .ticket-summary td { padding: 0.5mm 0; }
          .ticket-total { font-weight: 700; font-size: 12px; border-top: 2px solid #000; padding-top: 1mm; margin-top: 1mm; }
          .ticket-note { margin: 1.5mm 0; font-size: ${infoFontSize}px; }
          .ticket-warning { text-align: center; font-size: 10px; border: 1px solid #000; padding: 1mm; margin: 2mm 0; }
          .ticket-footer { margin-top: 2.5mm; text-align: center; font-size: 9.5px; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
        </style>
      </head>
      <body>
        ${
          ticketData.logoUrl
            ? `<div class="ticket-header"><img class="ticket-logo" src="${escapeHtml(ticketData.logoUrl)}" alt="Logo" /></div>`
            : ""
        }
        <div class="ticket-company">
          <h2>${escapeHtml(ticketData.warehouseName)}</h2>
          <p>${escapeHtml(ticketData.warehouseAddress)}</p>
          <p>Tel: ${escapeHtml(ticketData.warehousePhone)}${ticketData.warehouseEmail ? ` | ${escapeHtml(ticketData.warehouseEmail)}` : ""}</p>
        </div>
        <div class="ticket-title">=== TICKET DE COMMANDE ===</div>
        <div class="ticket-info">
          <table>
            <tbody>
              <tr>
                <td><b>Commande:</b> ${escapeHtml(order?.order_number || `#${order?.id || ""}`)}</td>
                <td class="text-right"><b>Table:</b> ${escapeHtml(order?.table_label || "-")}</td>
              </tr>
              <tr>
                <td><b>Date:</b> ${escapeHtml(formatDate(order?.order_date || new Date().toISOString()))}</td>
                <td class="text-right"><b>Couverts:</b> ${Number(order?.guest_count) || 0}</td>
              </tr>
              <tr>
                <td><b>Service:</b> ${escapeHtml(ticketData.serviceMode)}</td>
                <td class="text-right"><b>Serveur:</b> ${escapeHtml(order?.waiter_name || "Non renseigne")}</td>
              </tr>
              <tr>
                <td><b>Salle:</b> ${escapeHtml(ticketData.flowStatus)}</td>
                <td class="text-right"><b>Cuisine:</b> ${escapeHtml(ticketData.kitchenStatus)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="divider"></div>
        <div class="ticket-items">
          <table>
            <thead>
              <tr>
                <td style="width: 6%">#</td>
                <td style="width: 42%">Article</td>
                <td style="width: 16%">Qte</td>
                <td style="width: 16%" class="text-right">Prix</td>
                <td style="width: 20%" class="text-right">Total</td>
              </tr>
            </thead>
            <tbody>${itemRows}</tbody>
          </table>
        </div>
        <div class="ticket-summary">
          <table>
            <tbody>
              <tr>
                <td>Articles:</td>
                <td class="text-right">${ticketData.totalItems}</td>
              </tr>
              <tr>
                <td>Quantite:</td>
                <td class="text-right">${escapeHtml(formatQuantity(ticketData.totalQuantity))}</td>
              </tr>
            </tbody>
          </table>
          <div class="ticket-total">
            <table>
              <tbody>
                <tr>
                  <td>TOTAL COMMANDE:</td>
                  <td class="text-right">${formatAmount(ticketData.total)} CFA</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        ${
          order?.notes_front
            ? `<div class="ticket-note"><b>Note salle:</b> ${escapeHtml(order.notes_front)}</div>`
            : ""
        }
        ${
          order?.notes_kitchen
            ? `<div class="ticket-note"><b>Note cuisine:</b> ${escapeHtml(order.notes_kitchen)}</div>`
            : ""
        }
        <div class="ticket-warning">Commande prise - document non fiscal</div>
        <div class="ticket-footer">
          <div><b>Solutions ELSA GESTION</b></div>
          <div>Support: +226 64 98 90 99</div>
          <div>www.elsagestion.com</div>
          <div style="margin-top: 2mm; font-size: 8px;">
            ${escapeHtml(order?.order_number || `#${order?.id || ""}`)} | ${escapeHtml(formatDate(new Date().toISOString()))}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const printTicket = () => {
    const newWindow = window.open(
      "",
      "_blank",
      "width=300,height=600,toolbar=no,scrollbars=yes"
    );

    if (!newWindow) {
      console.error("Impossible d'ouvrir une fenetre d'impression.");
      return;
    }

    newWindow.document.open();
    newWindow.document.write(buildTicketHtml());
    newWindow.document.close();

    const triggerPrint = () => {
      newWindow.focus();
      newWindow.print();
    };

    const images = Array.from(newWindow.document.images || []);
    if (images.length === 0) {
      setTimeout(triggerPrint, 300);
      return;
    }

    let loaded = 0;
    const onResourceReady = () => {
      loaded += 1;
      if (loaded >= images.length) {
        setTimeout(triggerPrint, 200);
      }
    };

    images.forEach((image) => {
      if (image.complete) {
        onResourceReady();
      } else {
        image.onload = onResourceReady;
        image.onerror = onResourceReady;
      }
    });
  };

  return (
    <Modal
      title="Ticket de commande"
      open={visible}
      onCancel={onClose}
      width={320}
      centered
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={printTicket}
        >
          Imprimer
        </Button>,
      ]}
    >
      <div className="restaurant-order-ticket__format">
        <span>Format ticket:</span>
        <Select
          size="small"
          value={ticketWidthMm}
          onChange={setTicketWidthMm}
          style={{ width: 120 }}
          options={[
            { value: 58, label: "58 mm" },
            { value: 80, label: "80 mm" },
          ]}
        />
      </div>

      <div
        className="restaurant-order-ticket__preview"
        style={{
          maxWidth: previewMaxWidth,
          fontSize: isNarrowPaper ? "14px" : "13px",
          fontWeight: 700,
        }}
      >
        {ticketData.logoUrl ? (
          <div className="restaurant-order-ticket__logo-wrap">
            <img src={ticketData.logoUrl} alt="Logo" />
          </div>
        ) : null}

        <div className="restaurant-order-ticket__company">
          <h2>{ticketData.warehouseName}</h2>
          <p>{ticketData.warehouseAddress}</p>
          <p>
            Tel: {ticketData.warehousePhone}
            {ticketData.warehouseEmail ? ` | ${ticketData.warehouseEmail}` : ""}
          </p>
        </div>

        <div className="restaurant-order-ticket__title">
          === TICKET DE COMMANDE ===
        </div>

        <table className="restaurant-order-ticket__info">
          <tbody>
            <tr>
              <td>Commande: {order.order_number || `#${order.id || ""}`}</td>
              <td>Table: {order.table_label || "-"}</td>
            </tr>
            <tr>
              <td>Date: {formatDate(order.order_date)}</td>
              <td>Couverts: {Number(order.guest_count) || 0}</td>
            </tr>
            <tr>
              <td>Service: {ticketData.serviceMode}</td>
              <td>Serveur: {order.waiter_name || "Non renseigne"}</td>
            </tr>
          </tbody>
        </table>

        <div className="restaurant-order-ticket__divider" />

        <table className="restaurant-order-ticket__items">
          <thead>
            <tr>
              <td>#</td>
              <td>Article</td>
              <td>Qte</td>
              <td>Prix</td>
              <td>Total</td>
            </tr>
          </thead>
          <tbody>
            {ticketData.items.map((item, index) => {
              const quantity = Number(item?.quantity) || 0;
              const unitPrice = Number(item?.unit_price) || 0;
              return (
                <tr key={`${item?.id || item?.product_id || "item"}-${index}`}>
                  <td>{index + 1}</td>
                  <td>
                    {item?.product_name || `Produit ${item?.product_id || ""}`}
                    {item?.course_label ? <small>{item.course_label}</small> : null}
                    {item?.notes ? <small>Note: {item.notes}</small> : null}
                  </td>
                  <td>{formatQuantity(quantity, item?.unit_short_name)}</td>
                  <td>{formatAmount(unitPrice)}</td>
                  <td>{formatAmount(quantity * unitPrice)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="restaurant-order-ticket__summary">
          <div>
            <span>Articles</span>
            <strong>{ticketData.totalItems}</strong>
          </div>
          <div>
            <span>Quantite</span>
            <strong>{formatQuantity(ticketData.totalQuantity)}</strong>
          </div>
          <div className="restaurant-order-ticket__total">
            <span>Total commande</span>
            <strong>{formatAmount(ticketData.total)} CFA</strong>
          </div>
        </div>

        {order.notes_front ? (
          <div className="restaurant-order-ticket__note">
            Note salle: {order.notes_front}
          </div>
        ) : null}
        {order.notes_kitchen ? (
          <div className="restaurant-order-ticket__note">
            Note cuisine: {order.notes_kitchen}
          </div>
        ) : null}

        <div className="restaurant-order-ticket__warning">
          Commande prise - document non fiscal
        </div>
      </div>
    </Modal>
  );
};

export default RestaurantOrderTicket;
